import React, {
  lazy,
  PropsWithChildren,
  Suspense,
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";
import {
  CustomerProfileClientAssetProps,
  CustomerProfileModuleProps,
} from "redmond";
import { AgentBanner } from "halifax";
import {
  isCorpTenant,
  isVesperTenant,
  UserSourceProvider,
} from "@capone/common";
import { Route, Router, Switch } from "react-router-dom";
import { enableMapSet } from "immer";
import useWindowEventListener from "./hooks/useWindowEventListener";
import { eventsToListen } from "./utils/events";
import * as frenchTranslations from "./lang/fr.json";
import * as englishTranslations from "./lang/en.json";
import ExperimentsProvider from "./context/experiments";
import fetchUserInfo from "./api/v1/user/fetchUserInfo";
import AxiosInterceptors from "./components/AxiosInterceptors";
import { config } from "./api/config";

import { store } from "./store";
import { RewardsBanner } from "./modules/rewards/components";
import { PATH_HOME } from "./utils/paths";
import { CustomerProfileLanding } from "./pages/CustomerProfile";

function loadLocaleData(locale: string): any {
  switch (locale) {
    case "fr":
      return frenchTranslations;
    default:
      return englishTranslations;
  }
}
const generateClassName = createGenerateClassName({
  productionPrefix: "ptCustomerProfileModule",
  seed: "ptCustomerProfileModule",
});

export const ClientContext = React.createContext<
  Partial<CustomerProfileClientAssetProps>
>({});

const App = (props: CustomerProfileModuleProps) => {
  const {
    experiments,
    baseHistory,
    isAgentPortal,
    clientAssets,
    showCoreHomepage,
  } = props;
  // const { matchesMobile } = useDeviceTypes();
  const [activeTheme, setActiveTheme] = useState(props.theme);
  const [locale, setLocale] = useState(props.language);
  const [messages, setMessages] = useState(loadLocaleData(locale).default);
  const [sessionInfo, setSessionInfo] = useState(clientAssets.sessionInfo);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userInfoResponse = await fetchUserInfo();
        setSessionInfo(userInfoResponse);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUser();
  }, []);

  enableMapSet(); // TODO: Unsure if needed as no Adding/Remove is occuting

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
    console.log("THEME RECEIVED:", e.detail.palette.type);
  };
  const handleLocaleChanged = (e: CustomEvent) => {
    setLocale(e.detail);
    const messages = loadLocaleData(e.detail);
    setMessages(messages.default);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  useWindowEventListener(
    eventsToListen.HOST_LOCALE_CHANGED,
    handleLocaleChanged,
  );

  const { firstName, lastName } = sessionInfo?.userInfo || {
    firstName: "",
    lastName: "",
  };

  return (
    <Provider store={store}>
      <ExperimentsProvider initState={experiments}>
        <UserSourceProvider>
          <PortalComponentProvider>
            <Router history={baseHistory}>
              <AxiosInterceptors isAgentPortal={isAgentPortal} />
              <ClientContext.Provider value={{ ...clientAssets, sessionInfo }}>
                <div className="App">
                  <StylesProvider generateClassName={generateClassName}>
                    <ThemeProvider theme={activeTheme}>
                      {messages != null ? (
                        <IntlProvider
                          locale={locale}
                          defaultLocale="en"
                          messages={messages}
                        >
                          <Switch>
                            {isAgentPortal ? (
                              <Route path="*">
                                <AgentBanner
                                  firstName={firstName}
                                  lastName={lastName}
                                />
                              </Route>
                            ) : !showCoreHomepage ? (
                              <Route path="*">
                                <RewardsBanner />
                              </Route>
                            ) : null}
                          </Switch>
                          <Route path={PATH_HOME}>
                            <CustomerProfileLanding />
                          </Route>
                        </IntlProvider>
                      ) : (
                        <div>Loading</div>
                      )}
                    </ThemeProvider>
                  </StylesProvider>
                </div>
              </ClientContext.Provider>
            </Router>
          </PortalComponentProvider>
        </UserSourceProvider>
      </ExperimentsProvider>
    </Provider>
  );
};

export default App;

const PortalComponentProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const PortalComponent = useMemo(() => {
    if (isCorpTenant(config.TENANT)) {
      return lazy(
        () =>
          import(
            "../../../cap1-application/b2b-base/src/components/Core/CorporatePortal"
          ),
      );
    }
    if (isVesperTenant(config.TENANT)) {
      return lazy(
        () =>
          import(
            "../../../cap1-application/b2b-base/src/components/Core/VesperPortal"
          ),
      );
    }
    return lazy(
      () =>
        import("../../../cap1-application/b2b-base/src/components/Core/Portal"),
    );
  }, []);

  return (
    <Suspense fallback="loading">
      <PortalComponent>{children}</PortalComponent>
    </Suspense>
  );
};
